/* ── NcProductGallery ──────────────────────────── */
class NcProductGallery extends HTMLElement {
  static activeViewer = null;
  connectedCallback() {
    if (this._controller) return;
    this._controller = new AbortController();
    const { signal } = this._controller;
    this._viewport = this.querySelector('.nc-gallery__viewport');
    this._slides = Array.from(this.querySelectorAll('.nc-gallery__slide'));
    this._thumbs = Array.from(this.querySelectorAll('.nc-gallery__thumb'));
    this._currentIndex = 0;
    this._failed = false;
    if (!this._viewport || !this._slides.length) return;
    this._prev = this.querySelector('[data-gallery-prev]');
    this._next = this.querySelector('[data-gallery-next]');
    this._open = this.querySelector('[data-gallery-open]');
    this._status = this.querySelector('[data-gallery-status]');
    this._message = this.querySelector('[data-gallery-message]');
    this._message.hidden = true;
    this._open.removeAttribute('aria-busy');
    this.querySelector('.nc-gallery__toolbar').hidden = false;
    const thumbsRail = this.querySelector('.nc-gallery__thumbs');
    if (thumbsRail) thumbsRail.hidden = this._slides.length < 2;
    this._prev.hidden = this._next.hidden = this._slides.length < 2;
    for (const [index, slide] of this._slides.entries()) {
      slide.addEventListener('click', event => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || this._failed) return;
        event.preventDefault();
        this.#openViewer(index, slide);
      }, { signal });
    }
    for (const [index, thumb] of this._thumbs.entries()) {
      thumb.addEventListener('click', () => this.#goTo(index), { signal });
    }
    this._prev.addEventListener('click', () => this.#goTo(this._currentIndex - 1), { signal });
    this._next.addEventListener('click', () => this.#goTo(this._currentIndex + 1), { signal });
    this._open.addEventListener('click', () => this.#openViewer(this._currentIndex, this._open), { signal });
    this._viewport.addEventListener('scroll', () => {
      clearTimeout(this._scrollTimer);
      this._scrollTimer = setTimeout(() => this.#readPosition(), 100);
    }, { signal, passive: true });
    this.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const index = event.key === 'Home' ? 0 : event.key === 'End' ? this._slides.length - 1 : this._currentIndex + (event.key === 'ArrowRight' ? 1 : -1);
      this.#goTo(index);
      if (event.target.closest('.nc-gallery__thumb')) this._thumbs[this._currentIndex]?.focus({ preventScroll: true });
      else if (event.target.closest('.nc-gallery__slide')) this._slides[this._currentIndex]?.focus({ preventScroll: true });
    }, { signal });
    this._resize = new ResizeObserver(() => this.#goTo(this._currentIndex, false));
    this._resize.observe(this._viewport);
    this.#setActive(0, false);
  }

  disconnectedCallback() {
    this._controller?.abort();
    this._controller = null;
    this._resize?.disconnect();
    clearTimeout(this._scrollTimer);
    this._viewerController?.abort();
    this.#unlockPage();
    if (this._viewer) {
      this._viewer.element.style.display = 'none';
      this._viewer.element.inert = true;
      this._viewer.destroy();
    }
    this._opening = false;
    if (!this._viewer && NcProductGallery.activeViewer === this) NcProductGallery.activeViewer = null;
  }

  #setActive(index, announce = true) {
    const changed = index !== this._currentIndex;
    this._currentIndex = index;
    for (const [i, slide] of this._slides.entries()) {
      slide.tabIndex = i === index ? 0 : -1;
      slide.setAttribute('aria-hidden', String(i !== index));
    }
    for (const [i, thumb] of this._thumbs.entries()) {
      thumb.classList.toggle('active', i === index);
      thumb.setAttribute('aria-current', String(i === index));
      thumb.tabIndex = i === index ? 0 : -1;
    }
    this._prev.disabled = index === 0;
    this._next.disabled = index === this._slides.length - 1;
    this.querySelector('[data-gallery-counter]').textContent = (index + 1) + ' / ' + this._slides.length;
    if (changed && announce && !this._viewer) this._status.textContent = this.#label(index);
    this.#centerThumb(this._thumbs[index]);
  }

  #label(index) {
    return (this.dataset.galleryStatusTemplate || '').replace('[index]', String(index + 1)).replace('[length]', String(this._slides.length));
  }

  #centerThumb(thumb) {
    if (!thumb) return;
    const rail = thumb.parentElement;
    const box = thumb.getBoundingClientRect();
    const bounds = rail.getBoundingClientRect();
    rail.scrollTo({ left: rail.scrollLeft + box.left - bounds.left - (bounds.width - box.width) / 2, behavior: 'instant' });
  }

  #readPosition() {
    const left = this._viewport.getBoundingClientRect().left;
    let closest = 0;
    let distance = Infinity;
    for (const [index, slide] of this._slides.entries()) {
      const delta = Math.abs(slide.getBoundingClientRect().left - left);
      if (delta < distance) { distance = delta; closest = index; }
    }
    this.#setActive(closest);
  }

  #goTo(index, smooth = true) {
    index = Math.max(0, Math.min(this._slides.length - 1, index));
    const left = this._viewport.scrollLeft + this._slides[index].getBoundingClientRect().left - this._viewport.getBoundingClientRect().left;
    this._viewport.scrollTo({ left, behavior: smooth && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant' });
    this.#setActive(index);
  }

  async #openViewer(index, trigger) {
    if (this._opening || this._viewer || NcProductGallery.activeViewer) return;
    const controller = this._controller;
    this._opening = true;
    NcProductGallery.activeViewer = this;
    this._open.setAttribute('aria-busy', 'true');
    this._message.hidden = false;
    this._message.textContent = this.dataset.galleryLoading;
    try {
      const { default: PhotoSwipe } = await import(this.dataset.pswpModuleUrl);
      if (controller.signal.aborted) return;
      const duration = matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 250;
      const viewer = new PhotoSwipe({
        dataSource: this._slides.map(slide => {
          const img = slide.querySelector('img');
          return { src: slide.href, width: Number(slide.dataset.pswpWidth), height: Number(slide.dataset.pswpHeight), srcset: img.srcset, alt: img.alt, msrc: img.currentSrc || img.src };
        }),
        index, mainClass: 'nc-gallery-viewer', bgOpacity: 1, loop: false,
        initialZoomLevel: 'fit',
        secondaryZoomLevel: level => Math.max(level.fit, Math.min(1, level.fit * 2.5)),
        maxZoomLevel: level => Math.max(level.fit, Math.min(2, level.fit * 4)),
        wheelToZoom: true, escKey: true, trapFocus: true, returnFocus: false,
        clickToCloseNonZoomable: false, tapAction: () => {},
        showHideAnimationType: 'fade', showAnimationDuration: duration,
        hideAnimationDuration: duration, zoomAnimationDuration: duration,
        closeTitle: this.dataset.galleryClose, zoomTitle: this.dataset.galleryZoom,
        arrowPrevTitle: this.dataset.galleryPrevLabel, arrowNextTitle: this.dataset.galleryNextLabel,
        errorMsg: this.dataset.galleryImageError,
        paddingFn: size => size.y <= 480
          ? { top: 56, bottom: 12, left: 60, right: 60 }
          : size.x <= 640 ? { top: 64, bottom: 100, left: 12, right: 12 }
            : { top: 72, bottom: 112, left: 72, right: 72 }
      });
      this._viewer = viewer;
      const viewerController = new AbortController();
      this._viewerController = viewerController;
      viewer.on('openingAnimationEnd', () => {
        if (controller.signal.aborted) {
          queueMicrotask(() => viewer.destroy());
        }
      });
      this.#viewerUI(viewer);
      viewer.on('change', () => this.#goTo(viewer.currIndex, false));
      viewer.on('destroy', () => {
        viewerController.abort();
        this.#unlockPage();
        this._viewer = null;
        if (NcProductGallery.activeViewer === this) NcProductGallery.activeViewer = null;
        if (!controller.signal.aborted && trigger.isConnected) {
          const target = trigger.classList.contains('nc-gallery__slide') ? this._slides[this._currentIndex] : trigger;
          target.focus({ preventScroll: true });
        }
      });
      viewer.init();
      viewer.element.setAttribute('aria-label', this.dataset.galleryTitle);
      viewer.element.setAttribute('aria-modal', 'true');
      viewer.element.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          viewer.close();
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = Array.from(viewer.element.querySelectorAll('button, a[href], input, select, textarea, [tabindex]')).filter(element => {
          if (element.tabIndex < 0 || element.matches(':disabled') || !element.getClientRects().length) return false;
          for (let ancestor = element; ancestor && ancestor !== viewer.element; ancestor = ancestor.parentElement) {
            const style = getComputedStyle(ancestor);
            if (ancestor.hidden || ancestor.inert || style.display === 'none' || style.visibility !== 'visible' || Number(style.opacity) === 0) return false;
          }
          return true;
        });
        event.preventDefault();
        event.stopPropagation();
        if (!focusable.length) {
          viewer.element.focus({ preventScroll: true });
          return;
        }
        const current = focusable.indexOf(document.activeElement);
        const next = current < 0 ? (event.shiftKey ? focusable.length - 1 : 0) : (current + (event.shiftKey ? -1 : 1) + focusable.length) % focusable.length;
        focusable[next].focus({ preventScroll: true });
      }, { signal: this._viewerController.signal, capture: true });
      const updateZoomLabel = () => {
        const button = viewer.element.querySelector('.pswp__button--zoom');
        if (!button || !viewer.currSlide) return;
        const label = viewer.currSlide.currZoomLevel > viewer.currSlide.zoomLevels.initial + 0.01 ? this.dataset.galleryZoomOut : this.dataset.galleryZoomIn;
        button.title = label;
        button.setAttribute('aria-label', label);
      };
      viewer.on('zoomPanUpdate', updateZoomLabel);
      viewer.on('change', updateZoomLabel);
      updateZoomLabel();
      this.#lockPage(viewer.element);
      this._message.hidden = true;
    } catch (error) {
      if (controller.signal.aborted) return;
      this._viewer?.destroy();
      this._failed = true;
      this._message.textContent = this.dataset.galleryError;
    } finally {
      if (this._controller === controller) {
        this._opening = false;
        if (!this._viewer && NcProductGallery.activeViewer === this) NcProductGallery.activeViewer = null;
        this._open.removeAttribute('aria-busy');
      }
    }
  }

  #lockPage(viewerElement) {
    this._pageState = { x: window.scrollX, y: window.scrollY, overflow: document.documentElement.style.overflow, bodyOverflow: document.body.style.overflow, inert: [] };
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    for (const element of document.body.children) {
      if (!(element instanceof HTMLElement) || element === viewerElement) continue;
      this._pageState.inert.push([element, element.inert]);
      element.inert = true;
    }
  }

  #unlockPage() {
    if (!this._pageState) return;
    const state = this._pageState;
    document.documentElement.style.overflow = state.overflow;
    document.body.style.overflow = state.bodyOverflow;
    for (const [element, inert] of state.inert) element.inert = inert;
    window.scrollTo({ left: state.x, top: state.y, behavior: 'instant' });
    this._pageState = null;
  }

  #viewerUI(viewer) {
    viewer.on('uiRegister', () => {
      viewer.ui.registerElement({ name: 'gallery-title', className: 'nc-gallery-viewer__title', order: 5, appendTo: 'bar', onInit: el => { el.textContent = this.dataset.galleryTitle; } });
      viewer.ui.registerElement({ name: 'gallery-hint', className: 'nc-gallery-viewer__hint', appendTo: 'root', onInit: el => { el.textContent = matchMedia('(pointer: coarse)').matches ? this.dataset.galleryHint : this.dataset.galleryHintDesktop; } });
      if (this._slides.length < 2) return;
      viewer.ui.registerElement({ name: 'gallery-thumbs', className: 'nc-gallery-viewer__thumbs', appendTo: 'root', onInit: rail => {
        const buttons = this._slides.map((slide, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'nc-gallery-viewer__thumb';
          button.setAttribute('aria-label', this.#label(index));
          const img = document.createElement('img');
          const source = this._thumbs[index]?.querySelector('img') || slide.querySelector('img');
          img.src = source.currentSrc || source.src;
          img.alt = ''; img.width = 64; img.height = 52; img.loading = 'lazy';
          button.append(img);
          button.addEventListener('click', () => viewer.goTo(index), { signal: this._viewerController.signal });
          rail.append(button);
          return button;
        });
        const update = () => {
          for (const [index, button] of buttons.entries()) {
            button.setAttribute('aria-current', String(index === viewer.currIndex));
            button.tabIndex = index === viewer.currIndex ? 0 : -1;
          }
          this.#centerThumb(buttons[viewer.currIndex]);
        };
        rail.addEventListener('keydown', event => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault(); event.stopPropagation();
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : viewer.currIndex + (event.key === 'ArrowRight' ? 1 : -1);
          viewer.goTo(Math.max(0, Math.min(buttons.length - 1, next)));
          buttons[viewer.currIndex].focus({ preventScroll: true });
        }, { signal: this._viewerController.signal });
        viewer.on('change', update);
        update();
      } });
    });
  }
}
customElements.define('nc-product-gallery', NcProductGallery);

/* ── NcVariantPicker ───────────────────────────── */
class NcVariantPicker extends HTMLElement {
connectedCallback() {
  this._pills       = Array.from(this.querySelectorAll('.nc-variant-toggle__pill'));
  this._pills.forEach(pill => pill.addEventListener('click', () => this.#select(pill)));
}
#select(pill) {
  this._pills.forEach(p => { p.classList.remove('active'); p.setAttribute('aria-pressed', 'false'); });
  pill.classList.add('active');
  pill.setAttribute('aria-pressed', 'true');
  const vid       = pill.dataset.variantId;
  const url = new URL(window.location.href);
  url.searchParams.set('variant', vid);
  window.history.replaceState({}, '', url);
}
}
customElements.define('nc-variant-picker', NcVariantPicker);

/* ── NcProductTabs ─────────────────────────────── */
class NcProductTabs extends HTMLElement {
connectedCallback() {
  this._btns   = Array.from(this.querySelectorAll('.nc-tabs__btn'));
  this._panels = Array.from(this.querySelectorAll('.nc-tabs__panel'));
  this._btns.forEach((btn, idx) => {
    btn.addEventListener('click', () => this.#activate(idx));
    btn.addEventListener('keydown', e => this.#handleKey(e, idx));
  });
}
#activate(idx) {
  this._btns.forEach((b, i) => {
    const active = i === idx;
    b.classList.toggle('active', active);
    b.setAttribute('aria-selected', String(active));
    b.setAttribute('tabindex', active ? '0' : '-1');
  });
  this._panels.forEach((p, i) => {
    if (i === idx) p.removeAttribute('hidden');
    else p.setAttribute('hidden', '');
  });
}
#handleKey(e, idx) {
  const len = this._btns.length;
  let next = -1;
  if (e.key === 'ArrowRight') next = (idx + 1) % len;
  if (e.key === 'ArrowLeft')  next = (idx - 1 + len) % len;
  if (e.key === 'Home')       next = 0;
  if (e.key === 'End')        next = len - 1;
  if (next !== -1) { e.preventDefault(); this.#activate(next); this._btns[next].focus(); }
}
}
customElements.define('nc-product-tabs', NcProductTabs);

/* ── NcPriceAlert ──────────────────────────────── */
class NcPriceAlert extends HTMLElement {
connectedCallback() {
  this._openBtn   = this.querySelector('#nc-alert-open');
  this._modal     = this.querySelector('#nc-alert-modal');
  this._closeBtn  = this.querySelector('#nc-alert-close');
  this._form      = this.querySelector('#nc-alert-form');
  this._submitBtn = this.querySelector('#nc-alert-submit');
  this._toast     = this.querySelector('#nc-alert-toast');
  this._error     = this.querySelector('#nc-alert-error');
  this._productWrap = this.querySelector('#nc-alert-product-wrap');
  this._productInput = this.querySelector('#nc-alert-product-input');
  this._productHidden = this.querySelector('#nc-alert-product');
  const pdp       = document.getElementById('nc-pdp');
  this._title     = pdp?.dataset.productTitle || '';
  this._url       = window.location.origin + (pdp?.dataset.productUrl || '');
  this._id        = pdp?.dataset.productId   || '';
  this.#initProductAutocomplete();
  this._openBtn?.addEventListener('click', event => {
    event.preventDefault();
    this.#open();
  });
  this._closeBtn?.addEventListener('click', () => this.#close());
  this._modal?.addEventListener('click', e => {
    const r = this._modal.getBoundingClientRect();
    if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) this.#close();
  });
  this._modal?.addEventListener('cancel', e => {
    e.preventDefault();
    this.#close();
  });
  this._form?.addEventListener('submit', e => this.#handleSubmit(e));
  this._form?.querySelectorAll('[required]').forEach(input => {
    input.addEventListener('input', () => this.#clearFieldError(input, this.querySelector('#' + input.id + '-error')));
    input.addEventListener('change', () => this.#clearFieldError(input, this.querySelector('#' + input.id + '-error')));
  });
  if (new URLSearchParams(window.location.search).get('contact_posted') === 'true') this.#showToast();
}
#open() {
  if (!this._modal) return;
  if (window.NCA11y?.openDialog) {
    window.NCA11y.openDialog(this._modal, { trigger: this._openBtn, initialFocus: '#nc-alert-last-name' });
    return;
  }
  if (!this._modal.open) this._modal.showModal();
  this._form?.querySelector('#nc-alert-last-name')?.focus();
}
#close() {
  if (!this._modal?.open) return;
  if (window.NCA11y?.closeDialog) window.NCA11y.closeDialog(this._modal);
  else this._modal.close();
}
#showToast() {
  if (!this._toast) return;
  this._toast.classList.add('is-visible');
  setTimeout(() => this._toast.classList.remove('is-visible'), 6000);
}
#handleSubmit(e) {
  const lastNameInput = this._form.querySelector('#nc-alert-last-name');
  const firstNameInput = this._form.querySelector('#nc-alert-first-name');
  const emailInput = this._form.querySelector('#nc-alert-email');
  const phoneInput = this._form.querySelector('#nc-alert-phone');
  const cityInput = this._form.querySelector('[data-nc-city-combined]');
  const deadlineInput = this._form.querySelector('#nc-alert-deadline');
  const consentInput = this._form.querySelector('#nc-alert-consent');
  const bodyInput = this._form.querySelector('#nc-alert-body');
  const subjectInput = this._form.querySelector('#nc-alert-subject');

  if (this._error) this._error.hidden = true;
  if (this._productWrap?.ncCommitAutocompleteValue) this._productWrap.ncCommitAutocompleteValue();

  const TF = (window.NCI18n && window.NCI18n.form) || {};
  const TP = (window.NCI18n && window.NCI18n.pdp) || {};
  const TA = (window.NCI18n && window.NCI18n.autocomplete) || {};
  const validations = [
    this.#validateField(lastNameInput, this.querySelector('#nc-alert-last-name-error'), {
      required: TF.err_name || 'Veuillez renseigner votre nom.'
    }),
    this.#validateField(firstNameInput, this.querySelector('#nc-alert-first-name-error'), {
      required: TF.err_firstname || 'Veuillez renseigner votre prénom.'
    }),
    this.#validateField(emailInput, this.querySelector('#nc-alert-email-error'), {
      required: TF.err_email_required || 'Veuillez saisir votre adresse email.',
      email: TF.err_email_format || 'Veuillez saisir une adresse email valide.'
    }),
    this.#validateField(phoneInput, this.querySelector('#nc-alert-phone-error'), {
      required: TF.err_phone_required || 'Veuillez saisir votre numéro de téléphone.',
      tel: TF.err_phone_format || 'Veuillez saisir un numéro de téléphone valide.'
    }),
    this.#validateField(deadlineInput, this.querySelector('#nc-alert-deadline-error'), {
      required: TP.err_deadline || 'Veuillez choisir un horizon d\'achat.'
    })
  ];
  const productValid = !!this._productHidden?.value.trim();
  if (!productValid) {
    this.#setFieldError(this._productInput, this.querySelector('#nc-alert-product-input-error'), TF.err_product || 'Veuillez sélectionner au moins un produit.');
  } else {
    this.#clearFieldError(this._productInput, this.querySelector('#nc-alert-product-input-error'));
  }
  const consentValid = !!consentInput?.checked;
  if (!consentValid) {
    this.#setFieldError(consentInput, this.querySelector('#nc-alert-consent-error'), TP.err_consent_alert || 'Veuillez accepter d\'être contacté par mail pour recevoir les alertes de prix.');
  } else {
    this.#clearFieldError(consentInput, this.querySelector('#nc-alert-consent-error'));
  }

  if (!validations.every(Boolean) || !productValid || !consentValid) {
    e.preventDefault();
    this._form.querySelector('.is-error')?.focus();
    return;
  }
  this._submitBtn.disabled = true;
  this._submitBtn.querySelector('.spinner').style.display = 'block';

  const lastName = lastNameInput.value.trim();
  const firstName = firstNameInput.value.trim();
  const email = emailInput.value.trim();
  const phone = phoneInput.value.trim();
  const city = cityInput.value.trim();
  const deadline = deadlineInput.value.trim();
  const product = this._productHidden?.value.trim() || this._title;

  if (bodyInput) {
    bodyInput.value =
      'ALERTE DE PRIX\n================\n\n' +
      'Client : ' + firstName + ' ' + lastName + '\n' +
      'Email : ' + email + '\n' +
      'Telephone : ' + phone + '\n\n' +
      'Produits : ' + product + '\n' +
      'Ville de livraison : ' + city + '\n' +
      'Horizon d\'achat : ' + deadline + '\n' +
      'Consentement email : accepte\n\n' +
      'Lien produit : ' + this._url + '\n' +
      'ID Produit : ' + this._id;
  }
  if (subjectInput) subjectInput.value = 'Alerte de prix - ' + this._title;
}
#validateField(field, error, messages) {
  if (!field) return true;
  if (window.NCA11y?.validateField) return window.NCA11y.validateField(field, error, messages);

  const value = field.value.trim();
  const _TF = (window.NCI18n && window.NCI18n.form) || {};
  let message = '';
  if (field.required && !value) message = messages.required || _TF.err_required || 'Ce champ est obligatoire.';
  else if (field.type === 'email' && !field.validity.valid) message = messages.email || _TF.err_email_format || 'Veuillez saisir une adresse email valide.';
  else if (field.type === 'tel' && value.length < 8) message = messages.tel || _TF.err_phone_format || 'Veuillez saisir un numéro de téléphone valide.';

  if (message) {
    this.#setFieldError(field, error, message);
    return false;
  }
  this.#clearFieldError(field, error);
  return true;
}
#setFieldError(field, error, message) {
  if (!field) return;
  if (window.NCA11y?.setFieldError) {
    window.NCA11y.setFieldError(field, error, message);
  }
  field.classList.add('is-error');
  field.setAttribute('aria-invalid', 'true');
  field.setCustomValidity?.(message);
  if (error) {
    error.textContent = message;
    error.hidden = false;
  }
}
#clearFieldError(field, error) {
  if (!field) return;
  if (window.NCA11y?.clearFieldError) {
    window.NCA11y.clearFieldError(field, error);
  }
  field.classList.remove('is-error');
  field.removeAttribute('aria-invalid');
  field.setCustomValidity?.('');
  if (error) error.hidden = true;
}
#initProductAutocomplete() {
  const wrap = this._productWrap;
  const input = this._productInput;
  const list = this.querySelector('#nc-alert-product-list');
  const hidden = this._productHidden;
  if (!wrap || !input || !list || !hidden) return;

  let items = [];
  let origNames = [];
  const captureItems = () => {
    items = [...list.querySelectorAll('li[data-value]')];
    origNames = items.map(li => li.getAttribute('data-value'));
  };
  const ensureBuilt = () => {
    if (window.ncBuildAutocompleteList) window.ncBuildAutocompleteList(list);
    captureItems();
  };
  // Données chargées en différé (/?view=ac-data) : recapture si la liste est
  // construite de façon asynchrone après le 1er ensureBuilt.
  list.addEventListener('nc:autocomplete-built', captureItems);
  captureItems();
  const selectedBox = wrap.querySelector('[data-autocomplete-selected]');
  const selected = [];
  let activeIdx = -1;

  const escHtml = value => {
    const span = document.createElement('span');
    span.textContent = value;
    return span.innerHTML;
  };
  const normalize = value => value.trim().replace(/\s+/g, ' ');
  const clearActive = () => {
    for (const li of items) {
      li.classList.remove('is-active');
      li.setAttribute('aria-selected', 'false');
    }
    input.setAttribute('aria-activedescendant', '');
  };
  const close = () => {
    wrap.classList.remove('is-open');
    input.setAttribute('aria-expanded', 'false');
    activeIdx = -1;
    clearActive();
  };
  const syncHidden = () => {
    hidden.value = selected.map(item => item.name).join(', ');
    for (const li of items) {
      li.classList.toggle('is-selected', selected.some(item => item.name === li.getAttribute('data-value')));
    }
  };
  const filter = query => {
    query = query.toLowerCase().trim();
    list.querySelector('.nc-autocomplete__empty')?.remove();
    let hasVisible = false;

    items.forEach((li, index) => {
      const text = origNames[index].toLowerCase();
      const nameEl = li.querySelector('.nc-autocomplete__name');
      if (!nameEl) return;

      if (selected.some(item => item.name === origNames[index])) {
        li.classList.add('is-hidden');
      } else if (!query || text.includes(query)) {
        li.classList.remove('is-hidden');
        if (query) {
          const idx = text.indexOf(query);
          const orig = origNames[index];
          nameEl.innerHTML = escHtml(orig.slice(0, idx)) + '<mark>' + escHtml(orig.slice(idx, idx + query.length)) + '</mark>' + escHtml(orig.slice(idx + query.length));
        } else {
          nameEl.textContent = origNames[index];
        }
        hasVisible = true;
      } else {
        li.classList.add('is-hidden');
      }
    });

    if (!hasVisible && query) {
      const noResult = document.createElement('li');
      noResult.className = 'nc-autocomplete__empty';
      noResult.setAttribute('role', 'option');
      noResult.setAttribute('aria-disabled', 'true');
      const _TA2 = (window.NCI18n && window.NCI18n.autocomplete) || {};
      noResult.textContent = (_TA2.no_result || 'Aucun produit trouvé pour « %s »').replace('%s', query);
      list.appendChild(noResult);
    }
    activeIdx = -1;
    clearActive();
    wrap.classList.add('is-open');
    input.setAttribute('aria-expanded', 'true');
  };
  const renderSelected = () => {
    if (!selectedBox) return;
    selectedBox.innerHTML = '';
    for (const item of selected) {
      const chip = document.createElement('span');
      chip.className = 'nc-autocomplete__chip';

      const label = document.createElement('span');
      label.className = 'nc-autocomplete__chip-label';
      label.textContent = item.name;

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'nc-autocomplete__chip-remove';
      remove.textContent = 'x';
      const _TA = (window.NCI18n && window.NCI18n.autocomplete) || {};
      remove.setAttribute('aria-label', (_TA.remove_aria || 'Retirer %s').replace('%s', item.name));
      remove.addEventListener('click', () => {
        const index = selected.findIndex(selectedItem => selectedItem.name === item.name);
        if (index >= 0) selected.splice(index, 1);
        syncHidden();
        renderSelected();
        filter(input.value);
        input.focus();
      });

      chip.append(label, remove);
      selectedBox.appendChild(chip);
    }
  };
  const addValue = value => {
    value = normalize(value);
    if (!value) return;
    if (!selected.some(item => item.name === value)) selected.push({ name: value, quantity: 1 });
    input.value = '';
    this.#clearFieldError(input, this.querySelector('#nc-alert-product-input-error'));
    syncHidden();
    renderSelected();
    filter('');
    close();
  };
  const commitInput = () => {
    if (input.value.trim()) addValue(input.value);
    else syncHidden();
  };
  const highlight = visible => {
    clearActive();
    if (!visible[activeIdx]) return;
    visible[activeIdx].classList.add('is-active');
    visible[activeIdx].setAttribute('aria-selected', 'true');
    input.setAttribute('aria-activedescendant', visible[activeIdx].id || '');
    visible[activeIdx].scrollIntoView({ block: 'nearest' });
  };
  const selectItem = li => addValue(li.getAttribute('data-value'));
  const open = () => {
    ensureBuilt();
    filter(input.value);
    wrap.classList.add('is-open');
    input.setAttribute('aria-expanded', 'true');
  };

  wrap.ncCommitAutocompleteValue = commitInput;
  input.addEventListener('focus', open);
  input.addEventListener('click', open);
  input.addEventListener('input', () => filter(input.value));
  input.addEventListener('keydown', event => {
    const visible = [...list.querySelectorAll('li[data-value]:not(.is-hidden)')];
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      activeIdx = Math.min(activeIdx + 1, visible.length - 1);
      highlight(visible);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      activeIdx = Math.max(activeIdx - 1, 0);
      highlight(visible);
    } else if (event.key === 'Enter' && activeIdx >= 0 && visible[activeIdx]) {
      event.preventDefault();
      selectItem(visible[activeIdx]);
    } else if (event.key === 'Enter' && input.value.trim()) {
      event.preventDefault();
      commitInput();
    } else if (event.key === 'Escape') {
      close();
    }
  });
  document.addEventListener('click', event => {
    if (!wrap.contains(event.target)) close();
  });
  list.addEventListener('click', event => {
    const li = event.target.closest('li[data-value]');
    if (li) selectItem(li);
  });

  if ('requestIdleCallback' in window) requestIdleCallback(ensureBuilt, { timeout: 2500 });
  else setTimeout(ensureBuilt, 1200);

  addValue(wrap.dataset.currentProduct || this._title);
}
}
customElements.define('nc-price-alert', NcPriceAlert);

/* ── NcStickyCta ───────────────────────────────── */
class NcStickyCta extends HTMLElement {
connectedCallback() {
  const ctaRow    = document.querySelector('.nc-cta-row');
  if (ctaRow) {
    new IntersectionObserver(([entry]) => {
      const gone = !entry.isIntersecting;
      this.classList.toggle('is-visible', gone);
      if (gone) this.removeAttribute('inert');
      else this.setAttribute('inert', '');
    }, { threshold: 0 }).observe(ctaRow);
  }
}
}
customElements.define('nc-sticky-cta', NcStickyCta);

/* ── Spec cards scroll-reveal ──────────────────── */
{
const cards = Array.from(document.querySelectorAll('.nc-spec-card'));
if ('IntersectionObserver' in window && cards.length) {
  const obs = new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const idx = cards.indexOf(entry.target);
      setTimeout(() => entry.target.classList.add('revealed'), idx * 80);
      obs.unobserve(entry.target);
    }
  }, { threshold: 0.1 });
  for (const c of cards) obs.observe(c);
} else {
  for (const c of cards) c.classList.add('revealed');
}
}

/* --nc-header-h CSS var : fallback statique 138px (desktop) / 106px (mobile) défini
 dans assets/nc-common.css L230 + L235. Pas de calcul JS dynamique pour éviter le
 forced reflow signalé par PSI (lecture getBoundingClientRect + écriture setProperty). */
